import { isToolPart, RUN_CODE_TOOL_NAME, toolNameOf } from "@esi/shared";

/**
 * What a `run_code` part holds for its step row (openspec change `agent-turn`, D20): the program,
 * its note and files given, and the result as stored (output, errors, exit code, the files kept
 * as artefacts and those dropped). Read loosely: any field may be missing in an older or a
 * refused part, and nothing here is ever rendered as HTML.
 */

export interface CodeRunFile {
  artefact: string;
  version: number;
  name: string;
  size: number;
  mime: string;
  image: boolean;
}

export interface CodeRunResult {
  exitCode: number | null;
  stdout: string;
  stderr: string;
  durationMs: number | null;
  files: CodeRunFile[];
  dropped: { name: string; reason: string }[];
}

export interface CodeRunInput {
  language: string | null;
  code: string;
  note: string | null;
  /** The handles the model named (names come with the approval). */
  files: string[];
}

const str = (v: unknown) => (typeof v === "string" ? v : null);
const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);

export const isRunCodePart = (part: unknown): boolean =>
  isToolPart(part) && toolNameOf(part as { type: string }) === RUN_CODE_TOOL_NAME;

export function codeRunInputOf(input: unknown): CodeRunInput {
  const i = (typeof input === "object" && input !== null ? input : {}) as Record<string, unknown>;
  return {
    language: str(i.language),
    code: str(i.code) ?? "",
    note: str(i.note)?.trim() || null,
    files: Array.isArray(i.files) ? i.files.filter((f): f is string => typeof f === "string") : [],
  };
}

/** The run's result, or null when the part holds none (running, refused, failed). */
export function codeRunResultOf(output: unknown): CodeRunResult | null {
  const o = output as { external?: unknown; results?: unknown } | null;
  if (typeof o !== "object" || o === null || o.external !== true || !Array.isArray(o.results)) {
    return null;
  }
  const r = (o.results[0] ?? {}) as Record<string, unknown>;
  const files = Array.isArray(r.files) ? r.files : [];
  const dropped = Array.isArray(r.dropped) ? r.dropped : [];
  return {
    exitCode: num(r.exitCode),
    stdout: str(r.stdout) ?? "",
    stderr: str(r.stderr) ?? "",
    durationMs: num(r.durationMs),
    files: files.flatMap((f) => {
      const x = f as Record<string, unknown>;
      const artefact = str(x.artefact);
      const name = str(x.name);
      return artefact && name && /^A[1-9][0-9]*$/.test(artefact)
        ? [
            {
              artefact,
              version: num(x.version) ?? 1,
              name,
              size: num(x.size) ?? 0,
              mime: str(x.mime) ?? "application/octet-stream",
              image: x.image === true,
            },
          ]
        : [];
    }),
    dropped: dropped.flatMap((d) => {
      const x = d as Record<string, unknown>;
      const name = str(x.name);
      return name ? [{ name, reason: str(x.reason) ?? "failed" }] : [];
    }),
  };
}

/** Whether code ran for this answer (a run that returned a result): the audience line says so. */
export function ranCode(parts: readonly unknown[]): boolean {
  return parts.some(
    (p) => isRunCodePart(p) && codeRunResultOf((p as { output?: unknown }).output) !== null,
  );
}

/** Text clipped to `max` characters, with how many were cut. */
export function clipText(text: string, max: number): { text: string; cut: number } {
  return text.length <= max
    ? { text, cut: 0 }
    : { text: text.slice(0, max), cut: text.length - max };
}
